import type {
  Attributes,
  ExceptionContext,
  LoggerConfig,
  LoggerProvider,
  Span,
  SpanOptions,
} from './types';

/**
 * Main Logger class
 * Delegates all operations to the configured provider
 */
export class Logger implements LoggerProvider {
  private provider: LoggerProvider;
  private enabled: boolean;

  constructor(config: LoggerConfig) {
    this.provider = config.provider;
    this.enabled = config.enabled !== false;
  }

  trace(message: string, attributes?: Attributes): void {
    if (this.enabled) {
      this.provider.trace(message, attributes);
    }
  }

  debug(message: string, attributes?: Attributes): void {
    if (this.enabled) {
      this.provider.debug(message, attributes);
    }
  }

  info(message: string, attributes?: Attributes): void {
    if (this.enabled) {
      this.provider.info(message, attributes);
    }
  }

  warn(message: string, attributes?: Attributes): void {
    if (this.enabled) {
      this.provider.warn(message, attributes);
    }
  }

  error(message: string, attributes?: Attributes): void {
    if (this.enabled) {
      this.provider.error(message, attributes);
    }
  }

  fatal(message: string, attributes?: Attributes): void {
    if (this.enabled) {
      this.provider.fatal(message, attributes);
    }
  }

  async startSpan<T>(
    name: string,
    options: SpanOptions,
    callback: (span: Span) => Promise<T>
  ): Promise<T> {
    if (this.enabled) {
      return this.provider.startSpan(name, options, callback);
    }
    // If disabled, still execute the callback but without tracing
    const noopSpan: Span = {
      setAttribute: () => {},
      setAttributes: () => {},
    };
    return callback(noopSpan);
  }

  captureException(error: Error, context?: ExceptionContext): void {
    if (this.enabled) {
      this.provider.captureException(error, context);
    }
  }

  /**
   * Convenience method for wrapping async functions with span tracing
   */
  async traceAsync<T>(
    name: string,
    op: string,
    fn: () => Promise<T>,
    attributes?: Attributes
  ): Promise<T> {
    return this.startSpan(name, { op, attributes }, async () => fn());
  }
}
